/**
 * Where an application's page goes back to: the screen it was opened from
 * (Today, the Tracker, Browse jobs…), carried in the router state as `from`.
 * Opened with no origin (a pasted link, a reload), it goes back to Today.
 */
import type { Location } from 'react-router-dom'

export interface FromState {
  from?: string
}

/** The router state that remembers the current screen as the way back. */
export function fromHere(location: Pick<Location, 'pathname' | 'search'>): FromState {
  return { from: `${location.pathname}${location.search}` }
}

/** The same, read at the moment of a click (for handlers outside the route's own hooks). */
export function fromCurrent(): FromState {
  return fromHere(window.location)
}

const LABELS: Array<[RegExp, string]> = [
  [/^\/dashboard\/?$/, 'Home'],
  [/^\/dashboard\/tracker/, 'Tracking'],
  [/^\/dashboard\/jobs/, 'Browse jobs'],
  [/^\/dashboard\/auto-apply/, 'Auto apply'],
  [/^\/dashboard\/profile/, 'Profile'],
  [/^\/dashboard\/settings/, 'Settings'],
]

/** The way back and what to call it. Only an in-app address is followed. */
export function backTarget(state: unknown): { to: string; label: string } {
  const from = (state as FromState | null)?.from
  const to = typeof from === 'string' && from.startsWith('/dashboard') && !from.startsWith('/dashboard/applications/') ? from : '/dashboard'
  const path = to.split('?')[0]!
  const label = LABELS.find(([pattern]) => pattern.test(path))?.[1] ?? 'Back'
  return { to, label }
}
